import { IMessage } from "@endpoints/types/message.type";
import { chatMediaObjectUrl } from "../../../endpoints/images.service";
import dayjs from "../../../lib/dayjs";
import React, { useEffect, useState } from "react";
import { IUser } from "@type/user.type";

type Attachment = NonNullable<IMessage["attachments"]>[number];

type MessageBubbleProps = {
  item: IMessage;
  receiverDetails: any;
  user: IUser;
  onReply?: (message: IMessage) => void;
};

const isImageAttachment = (attachment: Attachment) => {
  const mimeType = attachment?.mimeType || "";
  const fileName = attachment?.fileName || "";
  return (
    mimeType.startsWith("image/") ||
    /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(fileName)
  );
};

const MessageBubble = React.memo(
  ({ item, receiverDetails, user, onReply }: MessageBubbleProps) => {
    const [resolvedAttachmentUrls, setResolvedAttachmentUrls] = useState<
      Record<string, string>
    >({});
    const senderId = item?.senderId || item?.createdBy;
    const isSelf = (id?: string) => !!id && [user?._id, user?.userId].includes(id);
    const isSender = isSelf(senderId);
    const displayName = isSender
      ? user?.firstName + " " + user?.lastName
      : receiverDetails?.name;
    // (user?.userType === EnumUserTypes.RECRUITER ? user?._id : user?.userId);

    const replyTo = item?.replyTo;
    const replyAuthorName =
      isSelf(replyTo?.senderId)
        ? user?.firstName + " " + user?.lastName
        : receiverDetails?.name;

    useEffect(() => {
      let isMounted = true;
      const attachments = item?.attachments || [];

      const resolveUrls = async () => {
        const nextResolvedUrls: Record<string, string> = {};

        await Promise.all(
          attachments.map(async (attachment) => {
            if (!isImageAttachment(attachment)) return;

            if (attachment?.uid && attachment?.url) {
              nextResolvedUrls[attachment.uid] = attachment.url;
              return;
            }

            if (!attachment?.uid) return;

            try {
              const response = await chatMediaObjectUrl(attachment.uid);
              const url = response?.data?.url || response?.url || "";
              if (url) {
                nextResolvedUrls[attachment.uid] = url;
              }
            } catch (error) {
              console.error("Failed to resolve chat media url", error);
            }
          }),
        );

        if (isMounted) {
          setResolvedAttachmentUrls(nextResolvedUrls);
        }
      };

      resolveUrls();

      return () => {
        isMounted = false;
      };
    }, [item?.attachments]);

    return (
      <div
        className={`group relative flex flex-col w-[60%] rounded-md p-3 gap-2 ${
          isSender ? "bg-white ml-auto" : "bg-[#F1F5F9] mr-auto"
        }`}
      >
        {onReply ? (
          <button
            type="button"
            onClick={() => onReply(item)}
            className="absolute -top-2 right-2 hidden group-hover:flex items-center gap-1 rounded-full border border-gray-200 bg-white px-2 py-0.5 text-[10px] font-semibold text-light-black shadow-sm hover:text-charcoal"
            aria-label="Reply to message"
          >
            ↩ Reply
          </button>
        ) : null}
        {/* {user?._id} */}
        <div className="flex items-center w-max">
          <span className="text-[10px] text-light-black font-medium">
            {dayjs(item?.createdAt).format("hh:mm a DD MMM")}
          </span>
          <span className="text-[10px] font-semibold text-light-black">
            &nbsp; -&nbsp; {displayName}
          </span>
        </div>
        {replyTo ? (
          <div className="flex flex-col gap-0.5 rounded border-l-2 border-primary bg-black/5 px-2 py-1">
            <span className="text-[10px] font-semibold text-primary">
              {replyAuthorName}
            </span>
            <span className="text-[11px] text-light-black line-clamp-2 break-words">
              {replyTo.hasAttachment && !replyTo.preview
                ? "📎 Attachment"
                : replyTo.preview}
              {replyTo.hasAttachment && replyTo.preview ? " 📎" : ""}
            </span>
          </div>
        ) : null}
        {item?.message ? (
          <span className="text-xs font-medium text-charcoal whitespace-pre-wrap break-words">
            {item?.message}
          </span>
        ) : null}
        {item?.attachments?.length ? (
          <div className="flex flex-col gap-2">
            {item.attachments.map((attachment) => (
              <a
                key={attachment.uid}
                href={attachment.url}
                target="_blank"
                rel="noreferrer"
                className="block rounded-md border border-gray-200 bg-white overflow-hidden hover:shadow-sm"
              >
                {isImageAttachment(attachment) ? (
                  <img
                    src={
                      resolvedAttachmentUrls[attachment.uid] || attachment.url
                    }
                    alt={attachment.fileName || "attachment"}
                    className="max-h-60 w-full object-cover"
                  />
                ) : (
                  <div className="flex items-center gap-3 p-3">
                    <div className="h-10 w-10 rounded bg-[#F1F5F9] flex items-center justify-center text-[10px] font-semibold uppercase text-charcoal">
                      {attachment.fileName?.split(".").pop()?.slice(0, 4) ||
                        "FILE"}
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-charcoal truncate">
                        {attachment.fileName || "Attachment"}
                      </p>
                      <p className="text-[10px] text-light-black">
                        {attachment.mimeType || "Open file"}
                      </p>
                    </div>
                  </div>
                )}
              </a>
            ))}
          </div>
        ) : null}
        {isSender && item?.seen === true ? (
          <span className="self-end text-[10px] font-medium text-light-black">
            Seen
          </span>
        ) : null}
      </div>
    );
  },
);

export default MessageBubble;
